// docs/21 World genesis on the client: the `world_story.genesis` postcard decoded by the wasm
// (`decode_genesis`, serde snake_case, enums as variant names) and the small helpers the intro,
// epilogue and Saga screens share. The module blanks the arc's secret before players see it.
import { create } from 'zustand';
import { wasmModule } from '../sim/wasm';
import { ordinal } from './calendar';
import type { StoryCue } from './cues';

export type Tone = 'Grim' | 'Eerie' | 'Mythic' | 'Hopeful' | 'Rollicking';
export type ArcShape = 'Mystery' | 'Siege' | 'Succession' | 'Plague' | 'Heist' | 'Pilgrimage' | 'Rebellion' | 'Haunting' | 'Exodus';
export type PlaceSlot = 'Wild' | 'Road' | 'Crossing';
export type Scene = 'Night' | 'Storm' | 'Ruin' | 'Road' | 'Marsh' | 'Fire' | 'Stars' | 'Dawn';
export type Foe = 'Warband' | 'Ambush' | 'Beast';
/** Who fills the first battle (docs/46 OV1, fk-schema `People`): a band people for a Warband or an Ambush, Spider or Burrower for a Beast. */
export type People = 'Goblins' | 'Orcs' | 'Bandits' | 'Cultists' | 'DarkElves' | 'Dead' | 'Gnolls' | 'Lizardfolk' | 'Spider' | 'Burrower'
  // docs/109
  | 'Vampires' | 'Ghosts' | 'Mummies' | 'Demons' | 'Golems' | 'Elementals' | 'Djinn' | 'Coven' | 'Mimics' | 'Beholder' | 'Gorgons' | 'Minotaurs' | 'Cyclopes' | 'Drakes' | 'Vermin' | 'WinterCourt' | 'Fey' | 'Duergar' | 'MenAtArms';
export type StoryWeather = 'Clear' | 'Overcast' | 'Rain' | 'Storm' | 'Fog' | 'Snow' | 'Heat' | 'Sandstorm' | 'Blizzard' | 'Frost' | 'Sleet';
export type Ground = 'Mire' | 'Greenwood' | 'Autumnwood' | 'Pinewood' | 'Snowfield' | 'Dunes' | 'Highland' | 'Jungle' | 'Underdark' | 'Ashlands';
/** The realm's country (docs/23, fk-schema `Biome`). */
export type StoryBiome = 'Temperate' | 'Wetland' | 'Boreal' | 'Desert' | 'Highland' | 'Jungle' | 'Underdark' | 'Ashlands';
export type StorySeason = 'Spring' | 'Summer' | 'Autumn' | 'Winter';
/** When the story begins (docs/23 Y1). */
export interface Calendar { year: number; reckoning: string; seasons: string[]; season: StorySeason; week: number }
/** How the first named foe gets away (docs/22 O4); the client plays it in place of a fall. */
export type Escape = 'Smoke' | 'Water' | 'Burrow' | 'Steed' | 'Wings' | 'Shadow';

export interface Named { name: string; title: string }
export interface Place { name: string; text: string }
export interface Line { speaker: string; text: string }
export interface Panel { scene: Scene; heading: string; text: string; line: Line | null }
/** When and where the first battle is fought (docs/22 O3). */
export interface Stage { hour: number; weather: StoryWeather; ground: Ground }
/** The first battle leader's plot armour (docs/22 O4). */
export interface NamedFoe { armor: string; escape: Escape; getaway: string; vow: string }
/**
 * The first battle as the game builds it (docs/50, fk-schema `BattleBrief`), serde's JSON: an
 * enum with fields is an object with one key (`{ Relic: {...} }`, `{ Hold: { pulses } }`).
 */
export type Folk = 'Goblin' | 'Orc' | 'Bandit' | 'Cultist' | 'DarkElf' | 'Skeleton' | 'Zombie' | 'Gnoll' | 'Lizardfolk' | 'Wolf' | 'Spider' | 'Troll' | 'Ooze' | 'Beast'
  // docs/109
  | 'Vampire' | 'Ghost' | 'Mummy' | 'Demon' | 'Golem' | 'Elemental' | 'Djinn' | 'Witch' | 'Mimic' | 'Beholder' | 'Gorgon' | 'Minotaur' | 'Cyclops' | 'Drake' | 'Vermin' | 'WinterCourt' | 'Fey' | 'Duergar' | 'Soldier'
  // docs/117: the Thing Below's arms (the sand's only by the forge, on dunes)
  | 'Tentacles' | 'SandTentacles';
export interface BattleBriefView {
  cast: {
    leader: { folk: Folk; archetype: string; keywords: string[]; actions: Array<{ name: string; shout: string; verbs: string[] }> };
    squad: Array<{ folk: Folk; role: string; weight: 'Few' | 'Some' | 'Many' }>;
  };
  bearing: string;
  set_piece: {
    stakes: { goals: unknown[]; join: 'Either' | 'Both'; deadline: number | null; line: string };
    landmark: { name: string; anchor: string } | null;
    thing: { Relic: { name: string; look: string; anchor: string } } | { Task: { name: string; verb: string; look: string; work: string; anchor: string } } | null;
    ward: { name: string; look: string; anchor: string } | null;
    beats: Array<{ name: string; line: string; when: string; happens: unknown; at: string }>;
  };
}
export interface Genesis {
  realm: { name: string; epithet: string; tone: Tone; saying: string; biome: StoryBiome };
  ages: Array<{ name: string; text: string }>;
  places: { wild: Place; road: Place; crossing: Place };
  haven: Place;
  factions: Array<{ name: string; want: string }>;
  arc: { shape: ArcShape; premise: string; antagonist: Named; secret: string; mystery: string; stages: string[]; finale: string };
  campaign: { title: string; pitch: string; asker: string; place: PlaceSlot; kind: string };
  intro: Panel[];
  battle: {
    title: string; place: PlaceSlot; foe: Foe; people: People; leader: Named; cry: string; objective: string; victory: string; defeat: string; hook: string;
    stage: Stage; parley: Line[]; named_foe: NamedFoe;
    /** docs/50: who fights and what the fight is about; absent on stories from before it. */
    brief?: BattleBriefView;
    /** docs/50 SB3: the difficulty the opening was proved at. */
    difficulty_bp?: number;
  };
  calendar: Calendar;
  /** The direction pass's mid-scene beats (docs/43); absent or empty on older stories. */
  cues?: StoryCue[];
}

/** `world_story.state` / `.source` and `world_opening.phase` (fk-world saga.rs). */
export const STORY = { WRITING: 0, TOLD: 1, FAILED: 2 } as const;
export const SOURCE = { TEMPLATE: 0, STORYTELLER: 1 } as const;
export const OPENING = { WAITING: 0, FIGHTING: 1, DONE: 2 } as const;
/** The ledger NPC of the World's first named foe (fk-world regard.rs `OPENING_NAMED_FOE_NPC`). */
export const OPENING_NAMED_FOE_NPC = 1100n;
/** The region kit for each story ground (fk-schema `Ground::kit`). */
export const GROUND_KIT: Record<Ground, string> = {
  Mire: 'mirewood', Greenwood: 'forest', Autumnwood: 'autumn', Pinewood: 'pinewood', Snowfield: 'winterwood', Dunes: 'dunes', Highland: 'highland', Jungle: 'jungle', Underdark: 'underdark', Ashlands: 'ashlands',
};
/** `outcome_row_code`: Success 0, PartialSuccess 1, SoftFail 2, Abandoned 3, Wipe 4. */
export const wonRow = (row: number | undefined | null) => row === 0 || row === 1;

const cache = new Map<string, Genesis | null>();
/** Decode a story blob once (keyed by its bytes' length and a cheap hash). */
export function decodeGenesis(bytes: Uint8Array | undefined): Genesis | undefined {
  if (!bytes || bytes.length === 0) return undefined;
  let h = 2166136261;
  for (const b of bytes) { h ^= b; h = Math.imul(h, 16777619); }
  const key = `${bytes.length}:${h >>> 0}`;
  if (!cache.has(key)) {
    try { cache.set(key, wasmModule().decode_genesis(bytes) as Genesis); } catch (err) { console.error('[story] decode_genesis failed', err); cache.set(key, null); }
  }
  return cache.get(key) ?? undefined;
}

/** The seeded template story for a phrase (the no-server harness). */
export function templateGenesis(phrase: string, name: string): Genesis {
  return wasmModule().template_genesis(phrase, name) as Genesis;
}

const SEASON_INDEX: Record<StorySeason, number> = { Spring: 0, Summer: 1, Autumn: 2, Winter: 3 };
/** "3rd week of Greenreed, 1347 Since the Sluice Was Raised" — when the story begins (docs/23). */
export function storyDate(c: Calendar | undefined): string {
  if (!c) return '';
  const name = c.seasons[SEASON_INDEX[c.season]] ?? c.season;
  return `${ordinal(c.week)} week of ${name}, ${c.reckoning.trim() ? `${c.year} ${c.reckoning.trim()}` : c.year}`;
}
export const seasonIndex = (s: StorySeason): number => SEASON_INDEX[s];

/** "the Gloaming Reaches" → "The Gloaming Reaches" (names keep a lower-case article in prose). */
export const display = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export function placeOf(g: Genesis, slot: PlaceSlot): Place {
  return slot === 'Wild' ? g.places.wild : slot === 'Road' ? g.places.road : g.places.crossing;
}

/** "Wren", "Wren and Ash", "Wren, Ash and Bram". */
export function joinNames(names: string[]): string {
  if (names.length === 0) return 'friends';
  if (names.length === 1) return names[0]!;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** The one brace token the story may carry (fk-schema `HEROES_TOKEN`). */
export const withHeroes = (text: string, heroes: string[]) => text.split('{heroes}').join(joinNames(heroes));

// Per-World "seen" marks, kept in this browser: the intro (skipped or played through), the
// epilogue and each storybook chapter's scene (docs/86). The store mirrors localStorage so the route rules re-run when a mark changes. A mark
// names the World's id and its founding: ids start over when the module's data is cleared, and a
// new World must not inherit the marks of an old one with its id.
type MarkedWorld = { id: bigint; createdAt: { microsSinceUnixEpoch: bigint } };
const worldKey = (w: MarkedWorld) => `${w.id}.${w.createdAt.microsSinceUnixEpoch}`;
const introKey = (w: MarkedWorld) => `fk.intro.${worldKey(w)}`;
const epilogueKey = (w: MarkedWorld) => `fk.epilogue.${worldKey(w)}`;
const chapterKey = (w: MarkedWorld, n: number) => `fk.chapter.${worldKey(w)}.${n}`;
const get = (k: string) => { try { return localStorage.getItem(k) === '1'; } catch { return false; } };
const put = (k: string, v: boolean) => { try { if (v) localStorage.setItem(k, '1'); else localStorage.removeItem(k); } catch { /* private mode */ } };

interface StoryMarks {
  rev: number;
  introSeen(w: MarkedWorld): boolean;
  epilogueSeen(w: MarkedWorld): boolean;
  setIntroSeen(w: MarkedWorld, v?: boolean): void;
  setEpilogueSeen(w: MarkedWorld, v?: boolean): void;
  /** docs/86: the scene that opened storybook chapter `n`. */
  chapterSeen(w: MarkedWorld, n: number): boolean;
  setChapterSeen(w: MarkedWorld, n: number, v?: boolean): void;
}
export const useStoryMarks = create<StoryMarks>()((set, getState) => ({
  rev: 0,
  introSeen: (w) => get(introKey(w)),
  epilogueSeen: (w) => get(epilogueKey(w)),
  setIntroSeen(w, v = true) { put(introKey(w), v); set({ rev: getState().rev + 1 }); },
  setEpilogueSeen(w, v = true) { put(epilogueKey(w), v); set({ rev: getState().rev + 1 }); },
  chapterSeen: (w, n) => get(chapterKey(w, n)),
  setChapterSeen(w, n, v = true) { put(chapterKey(w, n), v); set({ rev: getState().rev + 1 }); },
}));
